import React, { useEffect, useState } from 'react';
import { supabase } from '../../auth';
import { motion } from 'framer-motion';

export const AuditLogsPage: React.FC = () => {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('audit_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      setLogs(data || []);
    } catch (err) {
      console.error('Failed to load audit logs:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between backdrop-blur-md bg-white/30 dark:bg-gray-800/30 p-6 rounded-2xl shadow-xl border border-white/20"
      >
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            System Audit Logs
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-2">
            Track and monitor all critical actions within the organization.
          </p>
        </div>
        <button 
          onClick={fetchLogs}
          className="px-4 py-2 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-600 rounded-xl transition-all duration-300 transform hover:scale-105"
        >
          Refresh Logs
        </button>
      </motion.div>

      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="backdrop-blur-xl bg-white/40 dark:bg-gray-900/40 rounded-3xl shadow-2xl border border-white/30 overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-100/50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-300 uppercase text-xs tracking-wider">
                <th className="p-4 font-semibold rounded-tl-3xl">Time</th>
                <th className="p-4 font-semibold">User ID</th>
                <th className="p-4 font-semibold">Action</th>
                <th className="p-4 font-semibold">Target Entity</th>
                <th className="p-4 font-semibold rounded-tr-3xl">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200/20">
              {loading ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-gray-500">
                    <div className="flex justify-center items-center space-x-2">
                      <div className="w-4 h-4 rounded-full bg-indigo-500 animate-bounce"></div>
                      <div className="w-4 h-4 rounded-full bg-indigo-500 animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-4 h-4 rounded-full bg-indigo-500 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-gray-500">No logs found.</td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="hover:bg-white/50 dark:hover:bg-gray-800/50 transition-colors duration-200">
                    <td className="p-4 text-sm whitespace-nowrap text-gray-700 dark:text-gray-300">
                      {new Date(log.created_at).toLocaleString()}
                    </td>
                    <td className="p-4 text-sm font-mono text-gray-600 dark:text-gray-400">
                      {log.user_id.substring(0, 8)}...
                    </td>
                    <td className="p-4">
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                        log.action === 'SOFT_DELETE' ? 'bg-red-100 text-red-700 dark:bg-red-900/30' :
                        log.action === 'CREATE' ? 'bg-green-100 text-green-700 dark:bg-green-900/30' :
                        log.action === 'UPDATE' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30' :
                        'bg-gray-100 text-gray-700 dark:bg-gray-800'
                      }`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-medium">{log.entity}</span> <br/>
                      <span className="text-xs text-gray-500">{log.entity_id}</span>
                    </td>
                    <td className="p-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                      {log.old_data || log.new_data ? 'View Data' : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};
